<template>
  <preloader-overlay :loading="loading">

    <div v-if="isShownNotification" :class="$style.notification">
      <cabinet-notification />
    </div>

    <div :class="$style.container">

      <div :class="$style.header">
        <responsive-controller>
          <template #tablet>
            <screen-header
              :title="$t('card.binding.title')"
              :history-mode="false"
              @navigate="handleNavigate"
            />
          </template>
          <template #default>
            <div>
              <navigate-button
                v-absolute
                v-left="-80"
                @navigate="handleNavigate"
              />
              <screen-title
                :title="$t('card.binding.title')"
              />
            </div>
          </template>
        </responsive-controller>
      </div>

      <div :class="$style.content">
        <h3 :class="$style.title">
          {{ $t('card.binding.form.title') }}
        </h3>
        <div :class="$style.form">
          <!-- @TODO: выполнить рефакторинг формы привязки карты -->
          <card-inputs-mix ref="CardInputs" />
        </div>
        <p :class="$style.text" v-html="displayCardBindingPrivacyPolicy"></p>
      </div>

      <div :class="$style.submit">
        <simple-button variant="primary" :disabled="!isValid" @click="handleSubmit">
          {{ $t('card.binding.submit') }}
        </simple-button>
      </div>

    </div>
  </preloader-overlay>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from 'vuex';

import HTTPService from '@/services/Http';

import { getPayterraPaymentData } from 'services/payterra';

import ScreenTitle from 'molecules/screen/ScreenTitle';
import ScreenHeader from 'molecules/screen/ScreenHeader';
import NavigateButton from 'molecules/common/NavigateButton';
import SimpleButton from 'molecules/common/SimpleButton';
import PreloaderOverlay from 'molecules/common/PreloaderOverlay';

import CardInputsMix from '@/components/cardElements/CardInputsMix';
import CabinetNotification from '@/components/main/CabinetNotification';

export default {
  name: 'CardBinding',
  components: {
    ScreenTitle,
    ScreenHeader,
    NavigateButton,
    SimpleButton,
    PreloaderOverlay,
    CardInputsMix,
    CabinetNotification,
  },
  created() {
    if (this.isCurrencyCrypto) {
      this.$router.push({ name: 'wallet.binding' });
      return;
    }

    this.loadData();
  },
  mounted() {
    this.isMounted = true;
  },
  data() {
    return {
      isMounted: false,
      loading: false,
    };
  },
  computed: {
    displayCardBindingPrivacyPolicy() {
      return this.$t('card.binding.privacy.policy', {
        link: this.getCardBindingPrivacyPolicy,
        amount: this.bindingAmount,
      });
    },
    isValid() {
      if (!this.isMounted) {
        return false;
      }

      if (this.isNeedIdentification) {
        return false;
      }

      return this.$refs.CardInputs?.isValid;
    },
    isShownNotification() {
      if (this.isNeedIdentification) {
        return true;
      }

      return this.projectPaymentsDisabled || !this.projectValidated;
    },
    ...mapGetters([
      'isDesktop',
      'isNeedIdentification',
      'projectPaymentsDisabled',
      'projectValidated',
      'getCardBindingPrivacyPolicy',
      'isCheckoutPay',
      'getShopKey',
      'isCurrencyCrypto',
    ]),
    ...mapGetters('cards', [
      'isEmptyCardList',
      'bindingAmount',
    ]),
  },
  methods: {
    async loadData() {
      this.loading = true;

      if (this.isCurrencyCrypto) {
        this.$router.push({ name: 'wallet.binding' });
        return;
      }

      await Promise.all([
        this.fetchCardList(),
        this.fetchBindingDetails(),
      ]);

      this.loading = false;
    },
    handleNavigate() {
      if (this.isEmptyCardList) {
        this.$router.push({ name: 'cabinet' });
      } else {
        this.$router.go(-1);
      }
    },
    // @TODO: вынести в сервис привязки карты
    // eslint-disable-next-line complexity
    async handleSubmit() {
      if (!this.isValid) {
        return;
      }

      this.loading = true;

      let CardInputsRef = this.$refs.CardInputs;
      const formattedCardPan = CardInputsRef.cardPan.replace(/\s/g, '');

      let requestData;

      if (this.isCheckoutPay) {
        try {
          requestData = await getPayterraPaymentData({
            card_data: {
              card_number: formattedCardPan,
              card_month: CardInputsRef.cardMonth,
              card_year: CardInputsRef.cardYear,
              cvv: this.$validate.isEmptyString(CardInputsRef.cvc) ? '000' : CardInputsRef.cvc,
            },
            shop_key: this.getShopKey,
          });

          requestData.month = CardInputsRef.cardMonth;
          requestData.year = CardInputsRef.cardYear;
        } catch (error) {
          console.log(error);

          this.$alert({
            type: 'warning',
            text: 'error.try.bind.again',
          });

          // значит первая карта
          if (this.isEmptyCardList) {
            this.$router.go(-1);
          } else {
            this.$router.push({ name: 'cardSelect' });
          }

          this.loading = false;
          return;
        }
      } else {
        requestData = {
          pan: formattedCardPan,
          month: CardInputsRef.cardMonth,
          year: CardInputsRef.cardYear,
          cvv: CardInputsRef.cvc,
        };
      }

      try {
        // привязка карты
        const { data } = await HTTPService.bindCard(requestData);

        // если карта уже привязана CARD_ALREADY_EXISTS
        // вывести сообщение с ошибкой "card.binding.error"
        // иначе кидаем запрос статуса привязки карты
        if (data.status === 'CARD_ALREADY_EXISTS') {
          this.loading = false;

          this.$alert({
            type: 'warning',
            text: 'card.already.exists',
          });
          return;
        }

        this.SET_LAST_CARD_BINDING_PAGE(this.$router.history.current.name);

        if (data.enrolled) {
          this.SET_CARD_BINDING_TOKEN(data.token);

          this.$router.push({
            name: 'threeDSecure',
            params: { data },
          });
          return;
        }

        // запрос статуса привязки карты
        this.getBindingCardStatus(data.token);
      } catch (error) {
        this.$alert({
          type: 'warning',
          text: error.status === 400
            ? 'card.binding.error'
            : 'error.try.bind.again',
        });

        // значит первая карта
        if (this.isEmptyCardList) {
          this.$router.go(-1);
        } else {
          this.$router.push({ name: 'cardSelect' });
        }

        this.loading = false;
      }
    },
    async getBindingCardStatus(token) {
      this.loading = true;

      try {
        const { data } = await HTTPService.getCardStatus(token);

        if (data.status === 'SUCCESS') {
          this.$alert({
            type: 'success',
            text: 'card.bound.successfully',
          });

          this.$router.push({
            name: this.isEmptyCardList ? 'payout' : 'cardSelectedMessage',
          });
          return;
        }

        this.$alert({
          type: 'warning',
          text: 'card.binding.rejected.by.bank',
        });

        this.loading = false;
      } catch (error) {
        this.$alert({
          type: 'warning',
          text: 'failed.contact.the.support',
        });

        this.loading = false;
      }

      await this.$nextTick;
      this.$refs.CardInputs.resetData();
    },
    ...mapMutations([
      'SET_CARD_BINDING_TOKEN',
      'SET_LAST_CARD_BINDING_PAGE',
    ]),
    ...mapActions('cards', ['fetchCardList', 'fetchBindingDetails']),
  },
};
</script>

<style lang="sass" module>
  .notification
    max-width: 940px
    margin: 0 auto

  .container
    +size(100%, auto)
    max-width: 500px
    margin: 0 auto
    padding: 20px 0 60px

    +responsive('large')
      padding: 60px 0

  .header
    position: relative
    margin-bottom: 20px
    padding: 0 20px

    +respond-to('small')
      padding: 0

  .content
    +base-sub-mount
    margin-bottom: 20px

  .form
    margin-bottom: 10px

    // @TODO: удалить после переработки поля ввода номера карты
    fieldset
      margin-top: -1.24rem

  .title
    +font-type('regular', 20, 24)
    margin-bottom: 20px

  .text
    +font-type('medium', 14, 20)
    color: var(--color-white)
    opacity: .5

    a
      display: inline
      text-decoration: underline

      &:hover
        text-decoration: none

  .submit
    +flexbox
    +justify-content(center)
    margin: 0 20px

    +respond-to('medium')
      margin: 0
</style>
